/**
 * Context for managing approval eligibility state and mutations
 * Provides a centralized way to update eligibility and approvals data
 */
import { createContext, useContext, useCallback, useRef } from "react";
import { useApprovalEligibility } from "./hooks/useApprovalEligibility";
import type { ApprovalConfigurationUIRules } from "../../../../types/types.Workflow";

export interface ApprovalEligibilityContextValue {
  eligible: boolean;
  enabled: boolean;
  isLoading: boolean;
  uiRules: ApprovalConfigurationUIRules[];
  triggerUpdate: () => Promise<void>;
  registerApprovalsMutate: (mutate: () => Promise<void>) => void;
}

const ApprovalEligibilityContext =
  createContext<ApprovalEligibilityContextValue | undefined>(undefined);

interface ApprovalEligibilityProviderProps {
  productId: string;
  children: JSX.Element;
}

export function ApprovalEligibilityProvider({
  productId,
  children,
}: ApprovalEligibilityProviderProps): JSX.Element {
  const {
    eligible,
    isLoading,
    enabled,
    uiRules,
    mutate: mutateEligibility,
  } = useApprovalEligibility(productId);

  // Store reference to approvals mutate function
  const approvalsMutateRef = useRef<(() => Promise<void>) | null>(null);

  const registerApprovalsMutate = useCallback((mutate: () => Promise<void>) => {
    approvalsMutateRef.current = mutate;
  }, []);

  const triggerUpdate = useCallback(async () => {
    // Mutate eligibility data
    await mutateEligibility();

    // Mutate approvals list if registered
    if (approvalsMutateRef.current) {
      await approvalsMutateRef.current();
    }
  }, [mutateEligibility]);

  return (
    <ApprovalEligibilityContext.Provider
      value={{
        eligible,
        isLoading,
        enabled,
        uiRules,
        triggerUpdate,
        registerApprovalsMutate,
      }}
    >
      {children}
    </ApprovalEligibilityContext.Provider>
  );
}

export function useApprovalEligibilityContext(): ApprovalEligibilityContextValue {
  const context = useContext(ApprovalEligibilityContext);
  if (context === undefined) {
    return {
      eligible: false,
      enabled: false,
      isLoading: false,
      uiRules: [],
      triggerUpdate: async () => {},
      registerApprovalsMutate: () => {},
    };
  }
  return context;
}
